/**
 * cliff-edges.ts — turn the discrete SC2 cliff-level grid (cliffs.bin) + the raw
 * heightmap into hard cliff-edge segments where we draw vertical rock faces.
 *
 * WHY: SC2's dramatic cliff walls live in the cliff-LEVEL data, not the synced
 * heightmap (a smoothed pathing echo). cliffs.bin was exported but never used; this
 * is the single source of truth for "where is an impassable rock face," shared so
 * the client renders faces and the server can re-derive the identical set for a QA
 * overlay. See docs/superpowers/specs/2026-06-29-cliff-faces-design.md.
 *
 * Edges are RENDER-ONLY (no collider/navmesh). The caller supplies the height field:
 * the server overlay passes raw heights (faces sit on the nav block line); the client
 * passes the exaggerated VISUAL heights so the faces grow tall in lockstep with the
 * dramatized terrain. A face spans min(low-cell corners) → max(high-cell corners) so it
 * always covers the full visible drop (the 1-cell terrain ramp at the step sits behind
 * the opaque face — no terrain pokes through, even when adjacent cells share corners).
 */

export const CLIFF_PLATEAU_LEVELS: ReadonlySet<number> = new Set([64, 128, 192]);
// Rise (m) below which a level change is a steppable lip, not a wall. Matches the
// navmesh walkableClimb (0.4m): anything a unit could step up is not a cliff.
export const WALL_MIN_RISE = 0.4;

// Cliff-step visual exaggeration (CLIENT-VISUAL height boost; see terrain.ts
// exaggerateCliffHeights). Level 64 is the base playable plane and stays put;
// 128/192 (and the intermediate ramp values) rise by their step count × the
// per-level metre boost. Shared here so BOTH the terrain mesh and the doodad
// flat-ground filter (doodad-flatness.ts) reason about the SAME exaggerated
// surface — a doodad floats exactly where this offset jumps across its footprint.
export const CLIFF_BASE_LEVEL = 64;
export const CLIFF_LEVEL_SPAN = 64;        // one cliff level = 64 raw level units
export const CLIFF_EXTRA_PER_LEVEL = 1.2;  // metres added per cliff level above base

/** Visual height (m) added at a cell of the given cliff level. 0 at/below base or off-map. */
export function cliffVisualOffset(level: number): number {
  const steps = Math.max(0, (level - CLIFF_BASE_LEVEL) / CLIFF_LEVEL_SPAN);
  return steps * CLIFF_EXTRA_PER_LEVEL;
}

export interface CliffEdge {
  ax: number; az: number;   // boundary segment endpoint A (grid coords: x=col, z=row)
  bx: number; bz: number;   // boundary segment endpoint B
  yLow: number;             // low cell surface height (raw world units)
  yHigh: number;            // high cell surface height
  nx: number; nz: number;   // outward horizontal face normal (high cell → low cell), unnormalized unit step
}

function isRamp(level: number): boolean {
  return level !== 0 && !CLIFF_PLATEAU_LEVELS.has(level);
}

export function extractCliffEdges(
  cliffLevels: Uint16Array,
  heights: Float32Array,
  wallMinRise: number = WALL_MIN_RISE,
): CliffEdge[] {
  const cellDim = Math.round(Math.sqrt(cliffLevels.length));
  const vertDim = cellDim + 1;
  const edges: CliffEdge[] = [];

  const lvl = (r: number, c: number): number => cliffLevels[r * cellDim + c]!;
  const corners = (r: number, c: number): [number, number, number, number] => {
    const i = r * vertDim + c;
    return [heights[i]!, heights[i + 1]!, heights[i + vertDim]!, heights[i + vertDim + 1]!];
  };
  // Average corner height — used to classify the step (which side is high, rise size).
  const cellHeight = (r: number, c: number): number => {
    const [a, b, cc, d] = corners(r, c);
    return (a + b + cc + d) / 4;
  };
  // Min/max corner heights — used to SPAN the face so it covers the full visible drop.
  const cellMin = (r: number, c: number): number => Math.min(...corners(r, c));
  const cellMax = (r: number, c: number): number => Math.max(...corners(r, c));

  // Emit a wall on the boundary between cell A=(rA,cA) and cell B=(rB,cB) when the
  // levels differ, neither is off-map, they aren't both ramps, and the height step
  // is a real wall (≥ wallMinRise). (ax,az)-(bx,bz) is the shared grid line.
  const consider = (
    rA: number, cA: number, rB: number, cB: number,
    ax: number, az: number, bx: number, bz: number,
  ): void => {
    const la = lvl(rA, cA), lb = lvl(rB, cB);
    if (la === lb) return;
    if (la === 0 || lb === 0) return;            // off-map → handled by skirt
    if (isRamp(la) && isRamp(lb)) return;        // ramp interior is walkable
    const ha = cellHeight(rA, cA), hb = cellHeight(rB, cB);
    const rise = Math.abs(ha - hb);
    if (rise < wallMinRise) return;              // steppable lip (incl. ramp meeting its plateau)
    const highIsA = ha > hb;
    const hiR = highIsA ? rA : rB, hiC = highIsA ? cA : cB;
    const loR = highIsA ? rB : rA, loC = highIsA ? cB : cA;
    edges.push({
      ax, az, bx, bz,
      yLow: cellMin(loR, loC),                   // floor of the low side
      yHigh: cellMax(hiR, hiC),                  // top of the high plateau (full drop)
      nx: highIsA ? (cB - cA) : (cA - cB),       // step from high cell toward low cell
      nz: highIsA ? (rB - rA) : (rA - rB),
    });
  };

  for (let r = 0; r < cellDim; r++) {
    for (let c = 0; c < cellDim; c++) {
      // Horizontal neighbour (c | c+1): boundary is the vertical grid line x=c+1, z∈[r,r+1].
      if (c + 1 < cellDim) consider(r, c, r, c + 1, c + 1, r, c + 1, r + 1);
      // Vertical neighbour (r | r+1): boundary is the horizontal grid line z=r+1, x∈[c,c+1].
      if (r + 1 < cellDim) consider(r, c, r + 1, c, c, r + 1, c + 1, r + 1);
    }
  }
  return edges;
}
